import { memo, useState, useCallback, useEffect } from 'react';
import { Mail, Loader2, CheckCircle, ArrowLeft, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Input,
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  RadioGroup,
  RadioGroupItem,
  Label,
  cn,
} from '@flote/ui';
import {
  useAuthContext,
  verifyCustomerAccount,
  useSendSmsVerification,
  useVerifySmsCode,
  tokenManager,
  useAnalytics,
  AnalyticsEvents,
} from '@flote/app-core';
import { formatPhoneNumber } from '@flote/utils';
import { useModal, ModalType } from '../../lib/modal';

type AuthStep = 'email' | 'loading' | 'method-choice' | 'sending-otp' | 'otp' | 'verifying' | 'complete';
type DeliveryMethod = 'email' | 'sms';

interface AuthResponse {
  success?: boolean;
  message?: string;
  companyName?: string;
  retryAfter?: number;
}

interface OTPResponse {
  success?: boolean;
  message?: string;
  jwt?: string;
}

export const AuthModal = memo(() => {
  const { closeModal, openModal } = useModal();
  const { verifyEmail, verifyOTP, refetch } = useAuthContext();
  const sendSmsVerification = useSendSmsVerification();
  const verifySmsCode = useVerifySmsCode();
  const { identify, track } = useAnalytics();

  const [currentStep, setCurrentStep] = useState<AuthStep>('email');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [selectedMethod, setSelectedMethod] = useState<DeliveryMethod>('email');
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      if (errorMessage) setErrorMessage('');
    },
    [errorMessage]
  );

  const handleOtpChange = useCallback(
    (value: string) => {
      setOtpCode(value);
      if (errorMessage) setErrorMessage('');
    },
    [errorMessage]
  );

  const handleEmailSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim() || isSubmitting) return;

      setIsSubmitting(true);
      setCurrentStep('loading');
      setErrorMessage('');
      track(AnalyticsEvents.SIGN_IN_STARTED, { email });

      try {
        const result = await verifyCustomerAccount(email);

        if (result.status === 200) {
          // Check if customer has SMS capability
          if (result.body && result.body.phone && result.body?.isSmsCapable) {
            const normalizedNumber = formatPhoneNumber(result.body.phone);
            if (normalizedNumber) {
              setPhoneNumber(normalizedNumber);
            }
          }
          setCurrentStep('method-choice');
        } else if (result.status === 404) {
          closeModal();
          openModal(ModalType.REGISTRATION_MODAL, { userEmail: email });
        } else if (result.status === 400 || result.status === 500) {
          setErrorMessage(result.body.message || 'Failed to verify account');
          setCurrentStep('email');
        }
      } catch (error) {
        console.error('Verify email error:', error);
        setErrorMessage('Something went wrong. Please try again.');
        setCurrentStep('email');
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, isSubmitting]
  );

  const handleMethodSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (isSubmitting || resendCooldown > 0) return;

      const stepBeforeSending = currentStep;

      setIsSubmitting(true);
      setCurrentStep('sending-otp');
      setErrorMessage('');

      try {
        if (selectedMethod === 'email') {
          const result = await verifyEmail(email);
          const response = result.body as AuthResponse | undefined;

          if (result.status === 429) {
            const retryAfter = response?.retryAfter || 30;
            setResendCooldown(retryAfter);
            setErrorMessage(response?.message || 'Please wait before requesting another code');
            setCurrentStep(stepBeforeSending === 'otp' ? 'otp' : 'method-choice');
          } else if (response?.success) {
            track(AnalyticsEvents.SIGN_IN_OTP_SENT, { email, method: 'email' });
            setCurrentStep('otp');
            setResendCooldown(30);
          } else {
            const message = response?.message || 'Failed to send verification code';
            track(AnalyticsEvents.SIGN_IN_FAILED, { email, reason: message });
            setErrorMessage(message);
            setCurrentStep('method-choice');
          }
        } else {
          if (!phoneNumber || !phoneNumber.trim()) {
            setErrorMessage('Phone number is required for SMS verification');
            setCurrentStep('method-choice');
            setIsSubmitting(false);
            return;
          }

          const result = (await sendSmsVerification.mutateAsync({
            body: {
              email,
              phoneNumber,
            },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          })) as { status: 200 | 400 | 404 | 429 | 500; body: any };

          if (result.status === 429) {
            const retryAfter = result?.body?.retryAfter || 60;
            setResendCooldown(retryAfter);
            const waitTimeMinutes = Math.ceil(retryAfter / 60);
            const waitTimeDisplay = retryAfter < 60
              ? `${retryAfter} seconds`
              : waitTimeMinutes === 1
                ? '1 minute'
                : `${waitTimeMinutes} minutes`;
            setErrorMessage(`Please wait ${waitTimeDisplay} before requesting another code.`);
            setCurrentStep(stepBeforeSending === 'otp' ? 'otp' : 'method-choice');
          } else if (result.status === 200 && result.body.success) {
            track(AnalyticsEvents.SIGN_IN_OTP_SENT, { email, method: 'sms' });
            setCurrentStep('otp');
            setResendCooldown(60);
          } else {
            const message = result.body.message || 'Failed to send SMS verification code';
            track(AnalyticsEvents.SIGN_IN_FAILED, { email, reason: message });
            setErrorMessage(message);
            setCurrentStep('method-choice');
          }
        }
      } catch (error) {
        console.error('Send OTP error:', error);
        track(AnalyticsEvents.SIGN_IN_FAILED, { email, reason: 'OTP send error' });
        setErrorMessage('Failed to send verification code. Please try again.');
        setCurrentStep('method-choice');
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, phoneNumber, selectedMethod, isSubmitting, resendCooldown, currentStep, verifyEmail, sendSmsVerification]
  );

  const handleOtpSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!otpCode.trim() || otpCode.length !== 6 || isSubmitting) return;

      setIsSubmitting(true);
      setCurrentStep('verifying');
      setErrorMessage('');

      try {
        if (selectedMethod === 'email') {
          const result = await verifyOTP(email, otpCode);
          const otpResponse = result.body as OTPResponse | undefined;

          if (otpResponse?.success) {
            setCurrentStep('complete');
            toast.success('Successfully signed in');
          } else {
            const message = otpResponse?.message || 'Invalid verification code';
            track(AnalyticsEvents.SIGN_IN_FAILED, { email, reason: message });
            setErrorMessage(message);
            setCurrentStep('otp');
          }
        } else {
          const result = await verifySmsCode.mutateAsync({
            body: {
              email,
              phoneNumber,
              otp: otpCode,
            },
          });

          if (result.status === 200 && result.body.success) {
            if (result.body.jwt) {
              tokenManager.setToken(result.body.jwt);
              if (result.body.refreshToken) {
                tokenManager.setRefreshToken(result.body.refreshToken);
              }
              await refetch();

              // Identify user for analytics (mirrors email OTP flow in auth-context)
              if (result.body.customer) {
                const c = result.body.customer;
                identify({
                  id: String(c.id),
                  email: c.email ?? undefined,
                  companyId: c.companyNumber ?? undefined,
                  companyName: c.companyName ?? undefined,
                  customerId: c.id,
                });
                track(AnalyticsEvents.SIGN_IN_COMPLETED, {
                  userId: String(c.id),
                  email: c.email ?? '',
                  companyId: c.companyNumber,
                  companyName: c.companyName,
                });
              }
            }
            setCurrentStep('complete');
            toast.success('Successfully signed in');
          } else {
            const message = result.body.message || 'Invalid verification code';
            track(AnalyticsEvents.SIGN_IN_FAILED, { email, reason: message });
            setErrorMessage(message);
            setCurrentStep('otp');
          }
        }
      } catch (error) {
        console.error('Verify OTP error:', error);
        track(AnalyticsEvents.SIGN_IN_FAILED, { email, reason: 'OTP verification error' });
        setErrorMessage('Something went wrong. Please try again.');
        setCurrentStep('otp');
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, phoneNumber, otpCode, selectedMethod, isSubmitting, verifyOTP, verifySmsCode, refetch, identify, track]
  );

  const handleBackToEmail = useCallback(() => {
    setCurrentStep('email');
    setOtpCode('');
    setSelectedMethod('email');
    setPhoneNumber('');
    setErrorMessage('');
    setResendCooldown(0);
  }, []);

  const handleBackToMethodChoice = useCallback(() => {
    setCurrentStep('method-choice');
    setOtpCode('');
    setErrorMessage('');
    setResendCooldown(0);
  }, []);

  const handleComplete = useCallback(() => {
    closeModal();
  }, [closeModal]);

  return (
    <Dialog open={true} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Sign in to continue</DialogTitle>
          <DialogDescription>
            Enter your email to access your wholesale account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {currentStep === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  autoFocus
                  className="text-sm"
                />
                {errorMessage && <p className="text-xs text-red-600">{errorMessage}</p>}
              </div>

              <Button
                type="submit"
                disabled={!email.trim() || isSubmitting}
                className="w-full"
              >
                <Mail className="mr-2 h-4 w-4" />
                Continue
              </Button>
            </form>
          )}

          {currentStep === 'loading' && (
            <div className="flex items-center space-x-3 py-4">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <div>
                <h3 className="text-sm font-medium">Verifying account</h3>
                <p className="text-xs text-muted-foreground">Please wait a moment...</p>
              </div>
            </div>
          )}

          {currentStep === 'method-choice' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-sm font-medium">How would you like to receive your code?</h3>
                <p className="text-xs text-muted-foreground">Choose your preferred verification method.</p>
              </div>

              <form onSubmit={handleMethodSubmit} className="space-y-4">
                <RadioGroup
                  value={selectedMethod}
                  onValueChange={(value) => setSelectedMethod(value as DeliveryMethod)}
                  className="space-y-3"
                >
                  {/* Email Option */}
                  <div className="relative">
                    <Label
                      htmlFor="method-email"
                      className={cn(
                        "flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors",
                        selectedMethod === 'email'
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      <RadioGroupItem id="method-email" value="email" className="mt-0.5" />
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          <span className="text-sm font-medium">Email</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Send code to <span className="font-medium">{email}</span>
                        </p>
                      </div>
                    </Label>
                  </div>

                  {/* SMS Option - only show if phone number available */}
                  {phoneNumber && phoneNumber.trim().length > 0 && (
                    <div className="relative">
                      <Label
                        htmlFor="method-sms"
                        className={cn(
                          "flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors",
                          selectedMethod === 'sms'
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        )}
                      >
                        <RadioGroupItem id="method-sms" value="sms" className="mt-0.5" />
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <Smartphone className="h-4 w-4" />
                            <span className="text-sm font-medium">Text Message (SMS)</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Send code to <span className="font-medium">{phoneNumber}</span>
                          </p>
                        </div>
                      </Label>
                    </div>
                  )}
                </RadioGroup>

                {errorMessage && <p className="text-center text-xs text-red-600">{errorMessage}</p>}

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={handleBackToEmail} className="flex-1">
                    Back
                  </Button>
                  <Button type="submit" disabled={isSubmitting} className="flex-1">
                    {selectedMethod === 'email' ? (
                      <>
                        <Mail className="mr-2 h-4 w-4" />
                        Send Email
                      </>
                    ) : (
                      <>
                        <Smartphone className="mr-2 h-4 w-4" />
                        Send Text
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {currentStep === 'sending-otp' && (
            <div className="flex items-center space-x-3 py-4">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <div>
                <h3 className="text-sm font-medium">Sending verification code</h3>
                <p className="text-xs text-muted-foreground">
                  {selectedMethod === 'email'
                    ? `Sending code to ${email}...`
                    : `Sending text to ${phoneNumber}...`}
                </p>
              </div>
            </div>
          )}

          {currentStep === 'otp' && (
            <div className="space-y-4">
              <button
                onClick={handleBackToMethodChoice}
                className="mb-2 flex items-center text-xs text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="mr-1 h-3 w-3" />
                Change delivery method
              </button>

              <div className="space-y-2">
                <h3 className="text-sm font-medium">Enter verification code</h3>
                <p className="text-xs text-muted-foreground">
                  We sent a 6-digit code {selectedMethod === 'email' ? 'to' : 'via text to'}{' '}
                  <span className="font-medium">
                    {selectedMethod === 'email' ? email : phoneNumber}
                  </span>
                </p>
              </div>

              <form onSubmit={handleOtpSubmit} className="space-y-4">
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otpCode} onChange={handleOtpChange} autoFocus>
                    <InputOTPGroup className="gap-2">
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <p className="text-center text-xs text-muted-foreground">
                  Didn't receive the code?{' '}
                  {selectedMethod === 'email'
                    ? "Check your spam folder, search your inbox for 'flote', or "
                    : 'Wait 1-2 minutes, or '}
                  <button
                    type="button"
                    onClick={handleMethodSubmit}
                    disabled={resendCooldown > 0}
                    className={
                      resendCooldown > 0
                        ? 'cursor-not-allowed text-muted-foreground'
                        : 'text-primary underline hover:text-primary/80'
                    }
                  >
                    {resendCooldown > 0 ? `resend in ${resendCooldown}s` : 'resend'}
                  </button>
                  .
                </p>

                {errorMessage && <p className="text-center text-xs text-red-600">{errorMessage}</p>}

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={handleBackToMethodChoice} className="flex-1">
                    Back
                  </Button>
                  <Button type="submit" disabled={otpCode.length !== 6 || isSubmitting} className="flex-1">
                    Verify Code
                  </Button>
                </div>
              </form>
            </div>
          )}

          {currentStep === 'verifying' && (
            <div className="flex items-center space-x-3 py-4">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <div>
                <h3 className="text-sm font-medium">Verifying code</h3>
                <p className="text-xs text-muted-foreground">Please wait a moment...</p>
              </div>
            </div>
          )}

          {currentStep === 'complete' && (
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <CheckCircle className="mt-0.5 h-5 w-5 text-green-600" />
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">Welcome back!</h3>
                  <p className="text-xs text-muted-foreground">You've been successfully signed in.</p>
                </div>
              </div>

              <Button onClick={handleComplete} className="w-full">
                Continue
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});

AuthModal.displayName = 'AuthModal';
